import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pagination } from 'react-bootstrap';
import { toast } from 'react-toastify';
import clinicService from '../../services/clinic.service';
import { useEyeconxStore } from '../../store/eyeconxStore';
import Loading from '../common/Loading';
import './article-media-picker.css';

const ITEMS_PER_PAGE = 12;
const UPLOAD_INPUT_ID = 'articleMediaPickerUpload';

function isImageResource(item) {
    return item?.type?.startsWith('image/');
}

function isImageFile(file) {
    return file?.type?.startsWith('image/');
}

export default function AdminResourceImagePicker({ show, onHide, onSelect }) {
    const clinicId = useEyeconxStore((state) => state.clinicId);
    const uploadInputRef = useRef(null);
    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const loadMedia = useCallback(() => {
        if (!clinicId) {
            setError('Admin clinic is not available.');
            setMediaList([]);
            return Promise.resolve();
        }

        setLoading(true);
        setError(null);

        return clinicService
            .getMedia(clinicId)
            .then((res) => {
                setMediaList((res.data || []).filter(isImageResource));
            })
            .catch(() => {
                setError('Failed to load media library images.');
                setMediaList([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [clinicId]);

    useEffect(() => {
        if (!show) {
            return;
        }

        setSearchTerm('');
        setCurrentPage(1);
        setIsDragging(false);
        loadMedia();
    }, [show, loadMedia]);

    const filteredMedia = useMemo(() => {
        if (!searchTerm) {
            return mediaList;
        }

        const term = searchTerm.toLowerCase();
        return mediaList.filter((item) => item.name?.toLowerCase().includes(term));
    }, [mediaList, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(filteredMedia.length / ITEMS_PER_PAGE));
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredMedia.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleSelect = useCallback((item) => {
        onSelect(item.path, item);
        onHide();
    }, [onHide, onSelect]);

    const handleUpload = useCallback((file) => {
        if (!file || isUploading) {
            return;
        }

        if (!clinicId) {
            toast.error('Admin clinic is not available.', { position: toast.POSITION.BOTTOM_RIGHT });
            return;
        }

        if (!isImageFile(file)) {
            toast.error('Please upload an image file.', { position: toast.POSITION.BOTTOM_RIGHT });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);

        clinicService
            .uploadMedia(clinicId, formData, (progressEvent) => {
                const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                setUploadProgress(progress);
            })
            .then((res) => {
                toast.success('Image uploaded successfully', { position: toast.POSITION.BOTTOM_RIGHT });

                const uploaded = res.data;
                if (uploaded?.path && isImageResource(uploaded)) {
                    handleSelect(uploaded);
                    return;
                }

                loadMedia();
            })
            .catch(() => {
                toast.error('Upload failed. Please try again.', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsUploading(false);
                setUploadProgress(0);
                if (uploadInputRef.current) {
                    uploadInputRef.current.value = '';
                }
            });
    }, [clinicId, handleSelect, isUploading, loadMedia]);

    const paginationItems = [];
    for (let number = 1; number <= totalPages; number += 1) {
        paginationItems.push(
            <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
                {number}
            </Pagination.Item>
        );
    }

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable className="article-media-picker-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="h5 mb-0">
                    <i className="bi bi-images text-primary me-2"></i>
                    3ConX Media Library
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <p className="text-muted small mb-3">
                    Upload a new image or select one from the admin account media library.
                </p>

                <div
                    className={`article-media-picker__upload${isDragging ? ' article-media-picker__upload--dragging' : ''}${isUploading ? ' article-media-picker__upload--uploading' : ''}`}
                    onClick={() => !isUploading && uploadInputRef.current?.click()}
                    onDragOver={(event) => {
                        event.preventDefault();
                        setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(event) => {
                        event.preventDefault();
                        setIsDragging(false);
                        if (event.dataTransfer.files.length > 0) {
                            handleUpload(event.dataTransfer.files[0]);
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && !isUploading) {
                            uploadInputRef.current?.click();
                        }
                    }}
                >
                    <input
                        ref={uploadInputRef}
                        type="file"
                        className="d-none"
                        id={UPLOAD_INPUT_ID}
                        accept="image/*"
                        onChange={(event) => handleUpload(event.target.files?.[0])}
                        disabled={isUploading || !clinicId}
                    />

                    <i className="bi bi-cloud-arrow-up article-media-picker__upload-icon"></i>
                    <div className="article-media-picker__upload-title">
                        {isDragging ? 'Drop image here' : 'Upload image to admin library'}
                    </div>
                    <p className="article-media-picker__upload-hint mb-0">
                        Drag and drop or click to browse. JPG, PNG, GIF, and WebP supported.
                    </p>

                    {isUploading && (
                        <div className="article-media-picker__upload-progress">
                            <div className="progress" style={{ height: '6px' }}>
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                            <small className="text-muted d-block mt-1">Uploading: {uploadProgress}%</small>
                        </div>
                    )}
                </div>

                <div className="article-media-picker__toolbar">
                    <div className="input-group input-group-sm">
                        <span className="input-group-text">
                            <i className="bi bi-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search images by name..."
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value);
                                setCurrentPage(1);
                            }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setSearchTerm('');
                                    setCurrentPage(1);
                                }}
                                title="Clear search"
                            >
                                <i className="bi bi-x"></i>
                            </button>
                        )}
                    </div>
                    <span className="text-muted small">
                        {filteredMedia.length} image{filteredMedia.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {error && <div className="alert alert-warning border-0 py-2 px-3 small mb-3">{error}</div>}

                {loading ? (
                    <Loading />
                ) : filteredMedia.length > 0 ? (
                    <>
                        <div className="article-media-picker__grid">
                            {currentItems.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className="article-media-picker__item"
                                    onClick={() => handleSelect(item)}
                                    title={item.name}
                                >
                                    <div className="article-media-picker__thumb">
                                        <img src={item.path} alt={item.name} loading="lazy" />
                                    </div>
                                    <span className="article-media-picker__name">{item.name}</span>
                                </button>
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center mt-3">
                                <Pagination className="mb-0">
                                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                    <Pagination.Prev
                                        onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                                        disabled={currentPage === 1}
                                    />
                                    {paginationItems}
                                    <Pagination.Next
                                        onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    />
                                    <Pagination.Last
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="article-media-picker__empty">
                        <i className="bi bi-images"></i>
                        <p className="mb-0">
                            {searchTerm ? 'No images match your search.' : 'No images in the admin library yet. Upload one above.'}
                        </p>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
